// @flow
import React from "react";
import moment from "moment";
import classnames from "classnames";
import { keysIn, map, pick } from "ramda";
import fileDownload from "js-file-download";
import { Button } from '@driftt/dds'
import { Div, Span } from "@driftt/tide-core";
import { Table } from "@driftt/tide-table";
import { Icon } from "@driftt/tide-icons";

import { generateImportErrorCSV } from "api/imports";
import { ImportsTableColumns } from "./constants";

import "./style.css";

const TYPE_MAP = {
  SALESFORCE: "Salesforce Sync",
  DYNAMICS: "Microsoft Dynamics Sync",
  CSV: "CSV Upload",
};

const doCSVDownload = async (jobId) => {
  const { data } = await generateImportErrorCSV(jobId);
  fileDownload(data, "errors.csv");
};

const ErrorCell = ({ count, jobId }) => {
  return (
    <Div
      className="imports-table-error-cell"
      onClick={() => doCSVDownload(jobId)}
    >
      <Span>{count}</Span>
      <Button size="small" variant="secondary">
        Download
      </Button>
    </Div>
  );
};

const cellFormatter = (data, key, opts) => {
  const formatDateTime = (value) => moment(value).format("MM/DD/YYYY");

  const format = {
    synced: (value) =>
      !!value ? value : <Span className="no-account-created">{value}</Span>,
    jobCreatedAtUtc: formatDateTime,
    failed: (value) =>
      value && value !== "-" ? (
        <ErrorCell count={value} jobId={opts.jobId} />
      ) : (
        value
      ),
    fileName: (value) => {
      if (value) return value;

      const importType =
        data.type === "CSV Upload" ? "CSV Import" : data.type;

      return (
        <Span>
          {data.jobCreatedAtUtc
            ? `New ${importType} on ${formatDateTime(data.jobCreatedAtUtc)}`
            : `New ${importType}`}
        </Span>
      );
    },
    targeted: (value) =>
      value ? (
        <Icon
          className="imports-table-abm-targeted--yes"
          name="circle-check-2"
          width={26}
          height={26}
        />
      ) : (
        <Span className="imports-table-abm-targeted--no">—</Span>
      ),
    type: (value) => value,
    activeState: (value) =>
      typeof value === "string" ? value.toLowerCase() : "-",
  };

  return format[key] ? format[key](data[key]) : data[key].toString();
};

const formatAccountDataForTable = (imports) => {
  const format = (sync) => {
    let fileName = "";
    let targeted = false;

    const type = TYPE_MAP[sync.syncType.toUpperCase()];
    if (type === TYPE_MAP.CSV) {
      if (sync.listIds?.length > 0 &&
        /^[\],:{}\s]*$/.test(
          sync.listIds[0]
            .replace(/\\["\\/bfnrtu]/g, "@")
            .replace(
              /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/g,
              "]"
            )
            .replace(/(?:^|:|,)(?:\s*\[)+/g, "")
        )
      ) {
        const listIdObj = JSON.parse(sync.listIds[0]);
        fileName = listIdObj.accountListName || fileName;
        targeted = listIdObj.targeted;
      }
    }

    const flattenedSync = {
      ...sync,
      fileName,
      targeted,
      type,
    };
    const data = pick(
      [
        "fileName",
        "jobCreatedAtUtc",
        "type",
        "targeted",
        "activeState",
        "synced",
        "failed",
      ],
      flattenedSync
    );
    const toContent = (key) => ({
      content: cellFormatter(data, key, {
        jobId: sync.jobId,
        updateDateTime: sync.updateDteTime,
      }),
    });
    const columns = [...keysIn(data)];

    return { data: map(toContent, columns) };
  };

  return map(format, imports);
};

const ImportsTable = ({ imports = [], hasFinished }) => {
  return (
    <Div className="imports-table--container">
      <Table
        className={classnames("imports-table", {
          "imports-table--loaded": hasFinished,
        })}
        columns={ImportsTableColumns}
        data={formatAccountDataForTable(imports)}
      />
    </Div>
  );
};

export default ImportsTable;
